// src/services/storyService.ts
import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  Timestamp,
  FieldValue,
  getFirestore 
} from 'firebase/firestore';
import { auth } from '../firebase/firebase';
import { 
  getGuestStories, 
  getGuestStory, 
  saveGuestStory, 
  deleteGuestStory,
  generateGuestStoryId 
} from '../utils/guestStoryStorage';

// Initialize Firestore
const db = getFirestore();

interface CoverData {
  background: {
    type: string;
    value: string;
  };
  elements: Array<{
    id: string;
    type: string;
    content: string;
    x: number;
    y: number;
    fontSize?: number;
    fontFamily?: string;
    rotation?: number;
    scale?: number;
    color?: string;
  }>;
}

export interface StoryData {
  id?: string;
  title: string;
  type: 'graphic-novel' | 'regular-book' | 'picture-book';
  content?: any;
  description?: string;
  thumbnail?: string | CoverData | null;
  pageCount?: number;
  author?: string;
  userId?: string;
  createdAt?: Timestamp | string;
  lastModified?: Timestamp | string;
  lastEditedAt?: Timestamp | string;
  isPublished?: boolean;
  wasPublished?: boolean; 
  publishedAt?: Timestamp; 
  starred?: boolean; 
  tags?: string[];
  planData?: any; 
  storyPlan?: { [key: number]: string };
  isChallenge?: boolean;
  challengeType?: string;
  classId?: string | FieldValue;
  submittedAt?: Timestamp;
  status?: 'draft' | 'published' | 'returned';  // Add 'returned'
  assignmentId?: string;
assignmentTitle?: string;
assignmentDueDate?: Timestamp;
isLateSubmission?: boolean;
characters?: any[]; 
authorName?: string;
}

export class StoryService {
  
  // Create a new story (guest or authenticated)
  static async createStory(userId: string | null, storyData: Omit<StoryData, 'id' | 'userId' | 'createdAt' | 'lastModified'>): Promise<string> {
    try {
      if (!userId) {
        // GUEST MODE - Save to localStorage
        const storyId = generateGuestStoryId();
        const now = new Date().toISOString();
        
        saveGuestStory({
          id: storyId,
          title: storyData.title,
          type: storyData.type,
          thumbnail: storyData.thumbnail ? JSON.stringify(storyData.thumbnail) : undefined,
          content: storyData.content,
          planData: storyData.planData,
          createdAt: now,
          updatedAt: now
        });
        return storyId;
      }
      
      // AUTHENTICATED MODE - Save to Firebase (ROOT LEVEL)
      const storiesRef = collection(db, 'stories');
      const cleanData = Object.entries({
        ...storyData,
        userId,
        createdAt: serverTimestamp(),
        lastModified: serverTimestamp(),
        lastEditedAt: serverTimestamp()
      }).reduce((acc, [k, v]) => { if (v !== undefined) acc[k] = v; return acc; }, {} as any);
      const docRef = await addDoc(storiesRef, cleanData);
      return docRef.id;
    } catch (error) {
      console.error('Error creating story:', error);
      throw error;
    }
  }

  // Get all stories for a user (guest or authenticated)
// Get all stories for a user (guest or authenticated) - SUPPORTS BOTH LOCATIONS
static async getUserStories(userId: string | null): Promise<StoryData[]> {
  try {
    if (!userId) {
      // GUEST MODE - Load from localStorage
      const guestStories = getGuestStories();
      
      return guestStories.map(story => {
        // Parse content if it's a string
        let chapters = [];
        if (typeof story.content === 'string') {
          try {
            const parsed = JSON.parse(story.content);
            chapters = parsed.chapters || [];
          } catch (e) {
            console.warn('Could not parse story content:', e);
          }
        } else {
          chapters = story.content?.chapters || [];
        }
        
        // Calculate word count from chapters
        const wordCount = chapters.reduce((total: number, chapter: any) => {
          const text = (chapter.content || '').replace(/<[^>]*>/g, '');
          const words = text.trim().split(/\s+/).filter((w: string) => w.length > 0);
          return total + words.length;
        }, 0);
        
        return {
          id: story.id,
          title: story.title,
          type: 'regular-book' as const,
          content: { chapters },
          thumbnail: (story as any).coverData || story.thumbnail,
          planData: (story as any).planData || null,
          createdAt: story.createdAt,
          updatedAt: story.updatedAt,
          pageCount: chapters.length,
          wordCount: wordCount,
          isChallenge: false,
          tags: []
        };
      }) as StoryData[];
    }
    
    // AUTHENTICATED MODE - Load from BOTH locations and merge
    const allStories: StoryData[] = [];
    
    // 1. Load from NEW location (root level)
    try {
      const rootStoriesRef = collection(db, 'stories');
      const rootQuery = query(
        rootStoriesRef,
        where('userId', '==', userId),
        orderBy('lastModified', 'desc')
      );
      const rootSnapshot = await getDocs(rootQuery);
      
      rootSnapshot.docs.forEach(doc => {
        allStories.push({
          id: doc.id,
          ...doc.data()
        } as StoryData);
      });
      
      console.log('📚 Loaded from root /stories:', allStories.length);
    } catch (error) {
      console.warn('Could not load from root stories:', error);
    }
    
    // 2. Load from OLD location (subcollection)
    try {
      const oldStoriesRef = collection(db, 'users', userId, 'stories');
      const oldQuery = query(oldStoriesRef, orderBy('lastModified', 'desc'));
      const oldSnapshot = await getDocs(oldQuery);
      
      oldSnapshot.docs.forEach(doc => {
        allStories.push({
          id: doc.id,
          ...doc.data()
        } as StoryData);
      });
      
      console.log('📚 Loaded from /users/.../stories:', oldSnapshot.size);
    } catch (error) {
      console.warn('Could not load from user stories:', error);
    }
    
    console.log('📚 Total stories:', allStories.length);
    return allStories;
    
  } catch (error) {
    console.error('Error fetching user stories:', error);
    throw error;
  }
}

  // Get a specific story (guest or authenticated)
  static async getStory(userId: string | null, storyId: string): Promise<StoryData | null> {
    try {
      if (!userId) {
        // GUEST MODE - Load from localStorage
        const guestStory = getGuestStory(storyId);
        if (!guestStory) return null;
        
        console.log('📚 Raw guest story:', guestStory);
        
        // Parse content if it's a string
        let chapters = [];
        if (typeof guestStory.content === 'string') {
          try {
            const parsed = JSON.parse(guestStory.content);
            chapters = parsed.chapters || [];
          } catch (e) {
            console.warn('Could not parse story content:', e);
          }
        } else {
          chapters = guestStory.content?.chapters || [];
        }
        
        return {
          id: guestStory.id,
          title: guestStory.title,
          type: 'regular-book' as const,
          content: { chapters },
          thumbnail: (guestStory as any).coverData || guestStory.thumbnail,
          planData: (guestStory as any).planData || null,
          createdAt: guestStory.createdAt,
          updatedAt: guestStory.updatedAt
        } as StoryData;
      }
      
          // AUTHENTICATED MODE - Check BOTH locations
    const rootStoryRef = doc(db, 'stories', storyId);
    const rootStorySnap = await getDoc(rootStoryRef);

    if (rootStorySnap.exists()) {
      return {
        id: rootStorySnap.id,
        ...rootStorySnap.data()
      } as StoryData;
    }

    const userStoryRef = doc(db, 'users', userId, 'stories', storyId);
    const userStorySnap = await getDoc(userStoryRef);

    if (userStorySnap.exists()) {
      return {
        id: userStorySnap.id,
        ...userStorySnap.data()
      } as StoryData;
    }

    return null;
  } catch (error) {
    console.error('Error fetching story:', error);
    throw error;
  }
}

  // Update a story (guest or authenticated)
  static async updateStory(userId: string | null, storyId: string, updates: Partial<StoryData>): Promise<void> {
    const timestamp = new Date().toISOString();
    console.log(`🔵 [${timestamp}] updateStory CALLED`);
    console.log('   userId:', userId);
    console.log('   storyId:', storyId);
    console.log('   updates:', Object.keys(updates));
    
    try {
      if (!userId) {
        // GUEST MODE - Update in localStorage
        const existingStory = getGuestStory(storyId);
        if (!existingStory) {
          console.error('Story not found for guest:', storyId);
          return;
        }
        
        const updatedStory: any = {
          ...existingStory,
          ...updates,
          updatedAt: new Date().toISOString()
        };
        
        // Handle thumbnail - stringify if it's an object
        if (updates.thumbnail) {
          updatedStory.thumbnail = JSON.stringify(updates.thumbnail);
        }
        
        // Ensure createdAt is a string
        if (updatedStory.createdAt && typeof updatedStory.createdAt !== 'string') {
          updatedStory.createdAt = new Date().toISOString();
        }
        
        saveGuestStory(updatedStory);
        
        console.log(`✅ [${timestamp}] Guest story updated`);
        return;
      }
      
     // AUTHENTICATED MODE - Update in Firebase
// First check where the story is located
const rootStoryRef = doc(db, 'stories', storyId);
const userStoryRef = doc(db, 'users', userId, 'stories', storyId);

// Check root first
const rootSnap = await getDoc(rootStoryRef);
const storyRef = rootSnap.exists() ? rootStoryRef : userStoryRef;

console.log(`🔵 [${timestamp}] About to call updateDoc on:`, storyRef.path);
await updateDoc(storyRef, {
  ...updates,
  lastModified: serverTimestamp()
});
console.log(`✅ [${timestamp}] updateDoc completed`);
      
      // Published stories should be unpublished before editing
// Only sync when explicitly publishing/unpublishing
if (updates.isPublished !== undefined) {
  console.log(`🔵 [${timestamp}] Publishing/unpublishing story`);
}
      
    } catch (error) {
      console.error(`❌ [${timestamp}] Error updating story:`, error);
      throw error;
    }
  }

  // Update published story when main story is updated
  static async updatePublishedStory(userId: string, storyId: string, updates: Partial<StoryData>): Promise<void> {
    try {
      // Check if story is published (ROOT LEVEL)
      const storyRef = doc(db, 'stories', storyId);
      const storyDoc = await getDoc(storyRef);
      
      if (!storyDoc.exists()) return;
      
      const storyData = storyDoc.data();
      const publishedRef = doc(db, 'publishedStories', storyId);
      
      // If unpublishing, we don't update - Dashboard handles deletion
      if (updates.isPublished === false) return;
      
      // Only update published version if story is currently published
      if (storyData?.isPublished) {
        // Only update fields that should be synced
        const publishedUpdates: any = {};
        if (updates.title !== undefined) publishedUpdates.title = updates.title;
        if (updates.description !== undefined) publishedUpdates.description = updates.description;
        if (updates.thumbnail !== undefined) publishedUpdates.thumbnail = updates.thumbnail;
        if (updates.content !== undefined) publishedUpdates.content = updates.content;
        if (updates.pageCount !== undefined) publishedUpdates.pageCount = updates.pageCount;
        if (updates.tags !== undefined) publishedUpdates.tags = updates.tags;
        
        if (Object.keys(publishedUpdates).length > 0) {
          publishedUpdates.updatedAt = serverTimestamp();
          await updateDoc(publishedRef, publishedUpdates);
          console.log('✅ Published story synced');
        }
      }
    } catch (error) {
      console.error('Error updating published story:', error);
      // Don't throw - we don't want to break the save if this fails
    }
  }

  // Delete a story (guest or authenticated)
  static async deleteStory(userId: string | null, storyId: string): Promise<void> {
    try {
      if (!userId) {
        // GUEST MODE - Delete from localStorage
        deleteGuestStory(storyId);
        return;
      }
      
      // AUTHENTICATED MODE - Delete from Firebase (ROOT LEVEL)
      const storyRef = doc(db, 'stories', storyId);
      await deleteDoc(storyRef);
    } catch (error) {
      console.error('Error deleting story:', error);
      throw error;
    }
  }

  // Save story auto-save functionality (guest or authenticated)
  static async autoSaveStory(userId: string | null, storyId: string, content: any): Promise<void> {
    try {
      if (!userId) {
        // GUEST MODE - Update in localStorage
        const existingStory = getGuestStory(storyId);
        if (existingStory) {
          saveGuestStory({
            ...existingStory,
            content,
            updatedAt: new Date().toISOString()
          });
        }
        return;
      }
      
      // AUTHENTICATED MODE - Update in Firebase (ROOT LEVEL)
      const storyRef = doc(db, 'stories', storyId);
      await updateDoc(storyRef, {
        content,
        lastModified: serverTimestamp(),
        lastEditedAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error auto-saving story:', error);
      // Don't throw on auto-save errors to avoid disrupting user experience
    }
  }

  // Get stories by type (guest or authenticated)
  static async getStoriesByType(userId: string | null, type: StoryData['type']): Promise<StoryData[]> {
    try {
      if (!userId) {
        // GUEST MODE - Filter localStorage stories
        const allStories = getGuestStories();
        return allStories.filter(story => story.type === type).map(story => ({
          ...story,
          thumbnail: story.thumbnail ? JSON.parse(story.thumbnail as any) : undefined
        })) as StoryData[];
      }
      
      // AUTHENTICATED MODE - Query Firebase (ROOT LEVEL with filters)
      const storiesRef = collection(db, 'stories');
      const q = query(
        storiesRef,
        where('userId', '==', userId),
        where('type', '==', type),
        orderBy('lastModified', 'desc')
      );
      const querySnapshot = await getDocs(q);
      
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as StoryData));
    } catch (error) {
      console.error('Error fetching stories by type:', error);
      throw error;
    }
  }

  // Search stories (guest or authenticated)
  static async searchUserStories(userId: string | null, searchTerm: string): Promise<StoryData[]> {
    try {
      // Get all stories (works for both guest and authenticated)
      const stories = await this.getUserStories(userId);
      
      return stories.filter(story => 
        story.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (story.description && story.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (story.tags && story.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())))
      );
    } catch (error) {
      console.error('Error searching stories:', error);
      throw error;
    }
  }
}

// Hook for real-time story updates
export const useRealtimeStories = (userId: string | null) => {
  // This would use onSnapshot for real-time updates
  // Implementation depends on your specific needs
};

  

  